import { Box } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { P1R } from 'src/components';
import styled from '@emotion/styled';
import { themeColors } from 'src/theme';
import uploadFile from 'src/assets/fileDrop.svg';
import { useTranslation } from 'react-i18next';

const BORDER_COLOR = themeColors.black;
const BG_COLOR_NORMAL = themeColors.grayLight;
const BG_COLOR_DRAG = themeColors.blueLight;
const TEXT_COLOR_NORMAL = themeColors.black;
const TEXT_COLOR_DRAG = themeColors.grayDark;

const Container = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-width: 1px;
  border-radius: 4px;
  border-color: ${BORDER_COLOR};
  border-style: dotted;
  background-color: ${BG_COLOR_NORMAL};
  color: ${TEXT_COLOR_NORMAL};
  outline: none;
  transition:
    border 0.24s,
    background 0.24s ease-in-out;

  &.drag-active {
    background-color: ${BG_COLOR_DRAG};
    color: ${TEXT_COLOR_DRAG};
  }
`;

interface DropzoneProps {
  onDrop: (acceptedFiles: File[]) => void;
  onDropRejected?: (fileRejections: FileRejection[]) => void;
  onFileRejectionsChange?: (fileRejections: FileRejection[]) => void;
  maxFiles?: number;
  maxSize?: number;
  accept?: Record<string, string[]>;
  clickToSelectText?: string;
}

const Dropzone = ({
  onDrop,
  onDropRejected,
  onFileRejectionsChange,
  maxFiles = 1,
  maxSize,
  accept = {
    'text/csv': ['.csv'],
  },
}: DropzoneProps) => {
  const { t } = useTranslation();
  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      onDrop(acceptedFiles);
    },
    [onDrop],
  );

  const handleDropRejected = useCallback(
    (fileRejections: FileRejection[]) => {
      console.log('Dropzone rejected files:', fileRejections);
      if (onDropRejected) onDropRejected(fileRejections);
    },
    [onDropRejected],
  );

  const { getRootProps, getInputProps, isDragActive, acceptedFiles, fileRejections } = useDropzone({
    onDrop: handleDrop,
    onDropRejected: handleDropRejected,
    maxFiles,
    maxSize,
    accept,
  });

  useEffect(() => {
    if (onFileRejectionsChange) {
      onFileRejectionsChange([...fileRejections]);
    }
  }, [fileRejections, onFileRejectionsChange]);

  const showFileName = Array.isArray(acceptedFiles) && acceptedFiles.length > 0 && acceptedFiles[0]?.name;

  return (
    <Container
      {...getRootProps()}
      className={isDragActive ? 'drag-active' : ''}
      sx={{ '&:hover': { cursor: 'pointer', backgroundColor: 'rgba(200, 220, 250, 0.49)', borderColor: themeColors.blue } }}
    >
      <input {...getInputProps()} />
      {acceptedFiles.length ? null : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 6 }}>
          <img src={uploadFile} alt="upload" />
          <P1R columnGap="p" sx={{ textAlign: 'center', my: 2 }}>
            <Box component="span" sx={{ color: themeColors.blue }}>
              {t('bulkUpload.browseFiles')}{' '}
            </Box>
            {isDragActive ? t('bulkUpload.dragActiveText') : t('bulkUpload.dragInactiveText')}
          </P1R>

          <P1R sx={{ color: themeColors.grayPlaceholders, textAlign: 'center' }}>The maximum file upload is 50 MB</P1R>
        </Box>
      )}
      {showFileName ? acceptedFiles[0].name : null}
    </Container>
  );
};

export default Dropzone;
